// LocationSection.jsx
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { showErrorToast } from "../../../utils/notificationUtils";

const LocationSection = ({
  form,
  errors,
  countryOptions,
  cityOptions,
  setForm,
  handleInputChange,
}) => {
  const { t, i18n } = useTranslation();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const pendingCityRef = useRef(null);
  const mapInteractedRef = useRef(false);

  const normalize = (str) => (str || "").toString().trim().toLowerCase();

  const findOptionByName = (name, options) => {
    const n = normalize(name);
    if (!n || !options) return null;

    // Exact match
    let found = options.find((opt) => normalize(opt.label) === n);
    if (found) return found;

    // Option label includes the searched name
    found = options.find((opt) => normalize(opt.label).includes(n));
    if (found) return found;

    // Searched name includes the option label
    found = options.find((opt) => n.includes(normalize(opt.label)));
    return found || null;
  };

  const updateCoordinates = (lat, lng) => {
    setForm((prev) => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString(),
      map_coordinates: { lat, lng },
    }));
  };

  const applyGeocodedAddress = (address) => {
    if (!address || !mapInteractedRef.current) return;

    const countryName = address.country;
    const cityName =
      address.city ||
      address.town ||
      address.village ||
      address.municipality ||
      address.suburb ||
      address.district;
    const streetName =
      address.road ||
      address.street ||
      address.pedestrian ||
      address.footway ||
      address.highway ||
      "";
    const buildingNumber = address.house_number || "";

    setForm((prev) => {
      const updates = {};

      // Only fill fields the user hasn't already typed/selected manually
      if (countryName && !prev.country) {
        const countryOpt = findOptionByName(countryName, countryOptions);
        if (countryOpt) updates.country = countryOpt.value.toString();
      }

      if (cityName && !prev.city) {
        const cityOpt = findOptionByName(cityName, cityOptions);
        if (cityOpt) {
          updates.city = cityOpt.value.toString();
          pendingCityRef.current = null;
        } else {
          pendingCityRef.current = cityName;
        }
      }

      if (streetName && !prev.street?.trim()) updates.street = streetName;
      if (buildingNumber && !prev.buildingNumber?.trim()) updates.buildingNumber = buildingNumber;

      return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
    });
  };

  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
        { headers: { "Accept-Language": "en" } }
      );
      if (!response.ok) return;
      const data = await response.json();
      if (data?.address) applyGeocodedAddress(data.address);
    } catch (e) {
      console.error("Reverse geocoding failed:", e);
    }
  };

  const pinIcon = L.divIcon({
    className: "custom-leaflet-pin",
    html: `<svg viewBox="0 0 24 24" fill="#3b82f6" width="28" height="36" style="filter:drop-shadow(0 2px 2px rgba(0,0,0,0.25))"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg>`,
    iconSize: [28, 36],
    iconAnchor: [14, 36],
    popupAnchor: [0, -34],
  });

  const createOrMoveMarker = (lat, lng) => {
    const map = mapRef.current;
    if (!map) return;

    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      const marker = L.marker([lat, lng], { draggable: true, icon: pinIcon }).addTo(map);
      marker.on("dragend", (e) => {
        const { lat: newLat, lng: newLng } = e.target.getLatLng();
        mapInteractedRef.current = true;
        updateCoordinates(newLat, newLng);
        reverseGeocode(newLat, newLng);
      });
      markerRef.current = marker;
    }
  };

  // Initialize Leaflet map once
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const defaultCenter = [31.9539, 35.9106];
    const map = L.map(mapContainerRef.current).setView(defaultCenter, 15);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    mapRef.current = map;

    // Click to place/move marker
    map.on("click", (e) => {
      const { lat, lng } = e.latlng;
      mapInteractedRef.current = true;
      createOrMoveMarker(lat, lng);
      updateCoordinates(lat, lng);
      reverseGeocode(lat, lng);
    });

    // Place initial marker if coordinates exist
    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);
    if (!isNaN(lat) && !isNaN(lng)) {
      map.setView([lat, lng], 15);
      createOrMoveMarker(lat, lng);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
  }, []);

  // React to coordinate changes from outside the map (e.g. search result, props)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);

    if (!isNaN(lat) && !isNaN(lng)) {
      map.setView([lat, lng], 15);
      createOrMoveMarker(lat, lng);
    } else if (markerRef.current) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }
  }, [form.latitude, form.longitude]);

  // If the country changed and new city options loaded, try to match a pending city
  useEffect(() => {
    if (!pendingCityRef.current || !cityOptions?.length || !form.country) return;

    const cityOpt = findOptionByName(pendingCityRef.current, cityOptions);
    if (cityOpt) {
      setForm((prev) => ({ ...prev, city: cityOpt.value.toString() }));
      pendingCityRef.current = null;
    }
  }, [cityOptions, form.country]);

  const handleSearch = async () => {
    const query = form.mapSearchQuery?.trim();
    if (!query) return;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`,
        { headers: { "Accept-Language": i18n.language || "en" } }
      );
      const data = await response.json();

      if (data && data.length > 0) {
        mapInteractedRef.current = true;
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        updateCoordinates(lat, lng);
        if (data[0].address) applyGeocodedAddress(data[0].address);
      } else {
        showErrorToast(t("clinicSetup.alerts.locationNotFound"));
      }
    } catch (error) {
      console.error("Error searching location:", error);
      showErrorToast(t("clinicSetup.alerts.locationSearchError"));
    }
  };

  const handleClear = () => {
    pendingCityRef.current = null;
    setForm((prev) => ({
      ...prev,
      latitude: "",
      longitude: "",
      map_coordinates: {},
    }));

    const map = mapRef.current;
    if (markerRef.current && map) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }
  };

  return (
    <div
      style={{
        padding: "0",
        width: "100%",
        maxWidth: "960px",
        margin: "0 auto",
      }}
      dir={i18n.dir()}
      lang={i18n.language}
    >
      {/* Address Information Card */}
      <div style={{
        background: "#fff",
        padding: "24px",
        marginBottom: "24px"
      }}>
        <h3 style={{ 
          fontSize: "18px", 
          fontWeight: "600", 
          color: "#1f2937",
          marginBottom: "20px"
        }}>
          {t("clinicSetup.location.title")}
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            <div>
              <label style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "8px"
              }}>
                {t("clinicSetup.location.fields.country.label")} <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <select
                name="country"
                value={form.country}
              onChange={handleInputChange}
              style={{
                width: "100%",
                maxWidth: "450px",
                padding: "12px 16px",
                fontSize: "15px",
                border: errors.country ? "2px solid #ef4444" : "2px solid #e5e7eb",
                borderRadius: "10px",
                outline: "none",
                cursor: "pointer",
                background: "#fff",
                boxSizing: "border-box",
                transition: "all 0.2s ease",
                appearance: "none",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 16px center",
                  paddingRight: "40px"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.country ? "#ef4444" : "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              >
                <option value="" disabled>
                  {t("clinicSetup.location.fields.country.placeholder")}
                </option>
                {countryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.country && (
                <div style={{ color: "#ef4444", fontSize: "13px", marginTop: "6px" }}>
                  {errors.country}
                </div>
              )}
            </div>
            <div>
              <label style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "8px"
              }}>
                {t("clinicSetup.location.fields.city.label")} <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <select
                name="city"
                value={form.city}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  maxWidth: "450px",
                  padding: "12px 16px",
                  fontSize: "15px",
                  border: errors.city ? "2px solid #ef4444" : "2px solid #e5e7eb",
                  borderRadius: "10px",
                  outline: "none",
                  cursor: "pointer",
                  background: "#fff",
                  boxSizing: "border-box",
                  transition: "all 0.2s ease",
                  appearance: "none",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 16px center",
                  paddingRight: "40px"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.city ? "#ef4444" : "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              >
                <option value="" disabled>
                  {t("clinicSetup.location.fields.city.placeholder")}
                </option>
                {cityOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.city && (
                <div style={{ color: "#ef4444", fontSize: "13px", marginTop: "6px" }}>
                  {errors.city}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            <div>
              <label style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "8px"
              }}>
                {t("clinicSetup.location.fields.street.label")} <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="text"
                name="street"
                value={form.street}
                onChange={handleInputChange}
              placeholder={t("clinicSetup.location.fields.street.placeholder")}
              style={{
                width: "100%",
                maxWidth: "450px",
                padding: "12px 16px",
                  fontSize: "15px",
                  border: errors.street ? "2px solid #ef4444" : "2px solid #e5e7eb",
                  borderRadius: "10px",
                  outline: "none",
                  transition: "all 0.2s ease",
                  background: "#fff",
                  boxSizing: "border-box"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.street ? "#ef4444" : "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              />
              {errors.street && (
                <div style={{ color: "#ef4444", fontSize: "13px", marginTop: "6px" }}>
                  {errors.street}
                </div>
              )}
            </div>
            <div>
              <label style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "8px"
              }}>
                {t("clinicSetup.location.fields.buildingNumber.label")} <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="text"
                name="buildingNumber"
                value={form.buildingNumber}
                onChange={handleInputChange}
              placeholder={t("clinicSetup.location.fields.buildingNumber.placeholder")}
              style={{
                width: "100%",
                maxWidth: "450px",
                padding: "12px 16px",
                  fontSize: "15px",
                  border: errors.buildingNumber ? "2px solid #ef4444" : "2px solid #e5e7eb",
                  borderRadius: "10px",
                  outline: "none",
                  transition: "all 0.2s ease",
                  background: "#fff",
                  boxSizing: "border-box"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.buildingNumber ? "#ef4444" : "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              />
              {errors.buildingNumber && (
                <div style={{ color: "#ef4444", fontSize: "13px", marginTop: "6px" }}>
                  {errors.buildingNumber}
                </div>
              )}
            </div>
          </div>
          <div>
            <label style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              marginBottom: "8px"
            }}>
              {t("clinicSetup.location.fields.floorNumber.label")}
            </label>
            <input
              type="text"
              name="floorNumber"
              value={form.floorNumber}
              onChange={handleInputChange}
              placeholder={t("clinicSetup.location.fields.floorNumber.placeholder")}
              style={{
                width: "100%",
                maxWidth: "450px",
                padding: "12px 16px",
                fontSize: "15px",
                border: errors.floorNumber ? "2px solid #ef4444" : "2px solid #e5e7eb",
                borderRadius: "10px",
                outline: "none",
                transition: "all 0.2s ease",
                background: "#fff",
                boxSizing: "border-box"
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.floorNumber ? "#ef4444" : "#e5e7eb";
                e.target.style.boxShadow = "none";
              }}
            />
            {errors.floorNumber && (
              <div style={{ color: "#ef4444", fontSize: "13px", marginTop: "6px" }}>
                {errors.floorNumber}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Map Section Card */}
      <div style={{
        background: "#fff",
        padding: "24px"
      }}>
        <h3 style={{ 
          fontSize: "18px", 
          fontWeight: "600", 
          color: "#1f2937",
          marginBottom: "20px"
        }}>
          {t("clinicSetup.location.map.title")}
        </h3>
        <div style={{ marginBottom: "16px" }}>
          <label style={{
            display: "block",
            fontSize: "14px",
            fontWeight: "500",
            color: "#374151",
            marginBottom: "8px"
          }}>
            {t("clinicSetup.location.map.searchLabel")}
          </label>
          <div style={{
            display: "flex",
            gap: "12px"
          }}>
            <input
              type="text"
              name="mapSearchQuery"
              value={form.mapSearchQuery}
              onChange={handleInputChange}
              placeholder={t("clinicSetup.location.map.searchPlaceholder")}
              style={{
                flex: 1,
                padding: "12px 16px",
                fontSize: "15px",
                border: "2px solid #e5e7eb",
                borderRadius: "10px",
                outline: "none",
                transition: "all 0.2s ease",
                background: "#fff",
                boxSizing: "border-box"
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e5e7eb";
                e.target.style.boxShadow = "none";
              }}
            />
            <button
              type="button"
              onClick={handleSearch}
              style={{
                padding: "12px 24px",
                fontSize: "15px",
                fontWeight: "500",
                color: "#fff",
                background: "#3b82f6",
                border: "none",
                borderRadius: "10px",
                cursor: "pointer",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => (e.target.style.background = "#2563eb")}
              onMouseLeave={(e) => (e.target.style.background = "#3b82f6")}
            >
              {t("clinicSetup.buttons.search")}
            </button>
            <button
              type="button"
              onClick={handleClear}
              style={{
                padding: "12px 24px",
                fontSize: "15px",
                fontWeight: "500",
                color: "#6b7280",
                background: "#f3f4f6",
                border: "none",
                borderRadius: "10px",
                cursor: "pointer",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => (e.target.style.background = "#e5e7eb")}
              onMouseLeave={(e) => (e.target.style.background = "#f3f4f6")}
            >
              {t("clinicSetup.buttons.clear")}
            </button>
          </div>
        </div>
        <div
          style={{
            width: "100%",
            height: "450px",
            borderRadius: "12px",
            overflow: "hidden",
            border: "2px solid #e5e7eb",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          }}
        >
          <div
            ref={mapContainerRef}
            style={{ width: "100%", height: "100%" }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default LocationSection;
